// src/components/policies/AnnuityCard.jsx
// Displays a single annuity policy with surrender timeline

import React, { useState } from 'react';
import {
  PiggyBank, ChevronDown, ChevronUp, Edit2, Trash2, DollarSign,
  Calendar, User, Clock, TrendingUp, Loader2, AlertTriangle
} from 'lucide-react';
import { deleteAnnuityPolicy } from '../../api';
import AddAnnuityModal from './AddAnnuityModal';

const ANNUITY_TYPE_LABELS = {
  fixed: 'Fixed Annuity',
  fixed_indexed: 'Fixed Indexed Annuity',
  variable: 'Variable Annuity',
  immediate: 'Immediate Annuity',
  deferred: 'Deferred Annuity'
};

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-700',
  annuitized: 'bg-blue-100 text-blue-700',
  surrendered: 'bg-orange-100 text-orange-700',
  matured: 'bg-purple-100 text-purple-700'
};

export default function AnnuityCard({ policy, clientId, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this policy?')) return;

    try {
      setDeleting(true);
      await deleteAnnuityPolicy(clientId, policy.id);
      onUpdate?.();
    } catch (err) {
      console.error('[AnnuityCard] Delete error:', err);
      alert('Failed to delete policy');
    } finally {
      setDeleting(false);
    }
  };

  // Calculate surrender period progress
  const getSurrenderProgress = () => {
    if (!policy.effectiveDate || !policy.surrenderPeriodYears) return null;

    const startDate = new Date(policy.effectiveDate);
    const endDate = policy.surrenderEndDate
      ? new Date(policy.surrenderEndDate)
      : new Date(startDate.setFullYear(startDate.getFullYear() + policy.surrenderPeriodYears));
    const now = new Date();

    const totalDays = (endDate - new Date(policy.effectiveDate)) / (1000 * 60 * 60 * 24);
    const elapsedDays = (now - new Date(policy.effectiveDate)) / (1000 * 60 * 60 * 24);
    const progress = Math.min(Math.max((elapsedDays / totalDays) * 100, 0), 100);

    const daysRemaining = Math.max(Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)), 0);
    const yearsRemaining = Math.max(daysRemaining / 365, 0).toFixed(1);

    return {
      progress,
      endDate,
      daysRemaining,
      yearsRemaining,
      isComplete: progress >= 100
    };
  };

  const surrenderInfo = getSurrenderProgress();

  return (
    <>
      <div className="border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <PiggyBank className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-medium text-slate-800">{policy.carrier}</h4>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_STYLES[policy.status] || 'bg-slate-100 text-slate-600'}`}>
                  {policy.status}
                </span>
              </div>
              <p className="text-sm text-slate-500">
                {ANNUITY_TYPE_LABELS[policy.annuityType] || policy.annuityType}
                {policy.policyNumber && ` • ${policy.policyNumber}`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="font-semibold text-slate-800">{formatCurrency(policy.currentValue || policy.premiumAmount)}</p>
              {surrenderInfo && !surrenderInfo.isComplete && (
                <p className="text-xs text-orange-600">{surrenderInfo.yearsRemaining}y surrender</p>
              )}
            </div>
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </div>
        </div>

        {/* Expanded Details */}
        {expanded && (
          <div className="border-t border-slate-100 p-4 bg-slate-50">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
                  <DollarSign className="w-3.5 h-3.5" />
                  Premium
                </div>
                <p className="font-medium text-slate-800">{formatCurrency(policy.premiumAmount)}</p>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
                  <TrendingUp className="w-3.5 h-3.5" />
                  Current Value
                </div>
                <p className="font-medium text-slate-800">{formatCurrency(policy.currentValue)}</p>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
                  <Calendar className="w-3.5 h-3.5" />
                  Effective
                </div>
                <p className="font-medium text-slate-800">{formatDate(policy.effectiveDate)}</p>
              </div>
              {policy.monthlyIncome && (
                <div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
                    <DollarSign className="w-3.5 h-3.5" />
                    Monthly Income
                  </div>
                  <p className="font-medium text-slate-800">{formatCurrency(policy.monthlyIncome)}</p>
                </div>
              )}
            </div>

            {/* Surrender Period Timeline */}
            {surrenderInfo && (
              <div className="mb-4 p-4 bg-white rounded-xl border border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-emerald-500" />
                    Surrender Period
                  </h5>
                  {policy.currentSurrenderChargePct && policy.currentSurrenderChargePct > 0 && (
                    <span className="flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded-full">
                      <AlertTriangle className="w-3 h-3" />
                      {policy.currentSurrenderChargePct}% charge
                    </span>
                  )}
                </div>

                {/* Progress Bar */}
                <div className="relative h-3 bg-slate-100 rounded-full overflow-hidden mb-2">
                  <div
                    className={`absolute left-0 top-0 h-full rounded-full transition-all ${
                      surrenderInfo.isComplete
                        ? 'bg-gradient-to-r from-green-400 to-emerald-500'
                        : 'bg-gradient-to-r from-amber-400 to-orange-500'
                    }`}
                    style={{ width: `${surrenderInfo.progress}%` }}
                  />
                </div>

                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>{formatDate(policy.effectiveDate)}</span>
                  {surrenderInfo.isComplete ? (
                    <span className="text-green-600 font-medium">Surrender period complete!</span>
                  ) : (
                    <span>{surrenderInfo.daysRemaining} days remaining</span>
                  )}
                  <span>{formatDate(surrenderInfo.endDate)}</span>
                </div>
              </div>
            )}

            {/* Beneficiary */}
            {policy.beneficiaryName && (
              <div className="mb-4 p-3 bg-white rounded-lg border border-slate-200">
                <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
                  <User className="w-3.5 h-3.5" />
                  Beneficiary
                </div>
                <p className="font-medium text-slate-800">
                  {policy.beneficiaryName}
                  {policy.beneficiaryRelationship && (
                    <span className="text-slate-500 font-normal"> ({policy.beneficiaryRelationship})</span>
                  )}
                </p>
              </div>
            )}

            {/* Notes */}
            {policy.notes && (
              <div className="mb-4 p-3 bg-white rounded-lg border border-slate-200">
                <p className="text-sm text-slate-600">{policy.notes}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setShowEdit(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              >
                {deleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Delete
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <AddAnnuityModal
        isOpen={showEdit}
        onClose={() => setShowEdit(false)}
        clientId={clientId}
        policy={policy}
        onSuccess={() => {
          setShowEdit(false);
          onUpdate?.();
        }}
      />
    </>
  );
}
